
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Navigation, MapPin } from "lucide-react";
import { useBooking } from "@/hooks/useBooking";
import SearchBar from "@/components/search/SearchBar";
import ParkingSpotCard from "@/components/search/ParkingSpotCard";
import BookingDialog from "@/components/search/BookingDialog";
import MapView from "@/components/map/MapView";
import ViewToggle from "@/components/map/ViewToggle";
import RecentSearches from "@/components/search/RecentSearches";
import { Button } from "@/components/ui/button";
import { useRecentSearches } from "@/hooks/useRecentSearches";
import { openDirections } from "@/lib/directions";

interface ParkingSpot {
  id: string;
  owner_id: string;
  owner_name: string;
  address: string;
  vehicle_type: string;
  security_type: string;
  price_per_hour: number;
  parking_lot_count: number;
  images: string[];
  is_available: boolean;
  distance_km?: number;
  latitude?: number;
  longitude?: number;
}

const Search = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [spots, setSpots] = useState<ParkingSpot[]>([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [cardsVisible, setCardsVisible] = useState(false);
  const [searchType, setSearchType] = useState<'text' | 'location'>('text');
  const [currentView, setCurrentView] = useState<'list' | 'map'>('list');
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | undefined>();
  const { toast } = useToast();
  const { handleBooking } = useBooking();

  const { addSearch } = useRecentSearches();

  useEffect(() => {
    fetchParkingSpots();
  }, []);

  const fetchParkingSpots = async () => {
    try {
      setLoading(true);
      setCardsVisible(false);
      setSearching(true);
      console.log("Fetching parking spots with query:", searchQuery);
      
      // Simulate network delay for animation effect
      await new Promise(resolve => setTimeout(resolve, 1000));

      const { data, error } = await supabase.rpc('get_public_parking_spots', {
        search_text: searchQuery.trim() ? searchQuery.trim() : null,
      });

      if (error) {
        console.error("Supabase error:", error);
        throw error;
      }

      console.log("Fetched parking spots:", data);
      setSpots(data || []);
      
      // Delay showing cards for animation effect
      setTimeout(() => {
        setCardsVisible(true);
        setSearching(false);
      }, 300);
      
    } catch (error: any) {
      console.error("Error in fetchParkingSpots:", error);
      toast({
        title: "Error",
        description: "Failed to fetch parking spots. Please try again later.",
        variant: "destructive",
      });
      setSearching(false);
    } finally {
      setLoading(false);
    }
  };

  const fetchNearbySpots = async (latitude: number, longitude: number, radius: number = 10) => {
    try {
      setLoading(true);
      setCardsVisible(false);
      setSearching(true);
      setSearchType('location');
      setUserLocation({ lat: latitude, lng: longitude });
      console.log("Fetching nearby parking spots:", { latitude, longitude, radius });
      
      // Simulate network delay for animation effect
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const { data, error } = await supabase.rpc('get_nearby_parking_spots', {
        user_lat: latitude,
        user_lng: longitude,
        radius_km: radius
      });

      if (error) {
        console.error("Supabase RPC error:", error);
        throw error;
      }

      console.log("Fetched nearby parking spots:", data);
      setSpots(data || []);
      
      // Clear search query when using location search
      setSearchQuery('');
      
      // Delay showing cards for animation effect
      setTimeout(() => {
        setCardsVisible(true);
        setSearching(false);
      }, 300);
      
    } catch (error: any) {
      console.error("Error in fetchNearbySpots:", error);
      toast({
        title: "Error",
        description: "Failed to fetch nearby parking spots. Please try again later.",
        variant: "destructive",
      });
      setSearching(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchQuery.trim() !== '') {
        setSearchType('text');
        addSearch(searchQuery.trim());
        fetchParkingSpots();
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery]);

  const handleGetDirections = (spot: ParkingSpot) => {
    openDirections({ lat: spot.latitude, lng: spot.longitude, address: spot.address });
  };

  const getAnimationDelay = (index: number) => {
    return `${100 + (index * 100)}ms`;
  };

  const handleLocationSearch = (lat: number, lng: number) => {
    fetchNearbySpots(lat, lng);
  };

  const handleViewChange = (view: 'list' | 'map') => {
    setCurrentView(view);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-blue-50">
      <div className="container mx-auto px-4 py-8">
        <div className="animate-slide-in-bottom">
          <h1 className="text-3xl font-bold text-center mb-6 text-primary-800">Find Your Perfect Parking Spot</h1>
          <p className="text-center text-gray-600 mb-8 max-w-xl mx-auto">
            Search by location, owner name, or use "Near Me" to discover available parking spots around you.
          </p>
        </div>
        
        <SearchBar 
          searchQuery={searchQuery} 
          setSearchQuery={setSearchQuery}
          onLocationSearch={handleLocationSearch}
          showNearMe={true}
        />

        {!searchQuery && <RecentSearches onPick={(q) => setSearchQuery(q)} />}

        {/* View Toggle */}
        <div className="flex justify-center mb-6">
          <ViewToggle 
            currentView={currentView}
            onViewChange={handleViewChange}
            disabled={searching || loading}
          />
        </div>

        {searching && (
          <div className="flex flex-col justify-center items-center py-12 animate-bounce-in">
            <div className="relative w-20 h-20">
              <div className="absolute inset-0 rounded-full bg-blue-200 animate-ping opacity-25"></div>
              <div className="relative flex justify-center items-center w-full h-full">
                <MapPin className="h-10 w-10 text-blue-500 animate-pulse" />
              </div>
            </div>
            <p className="mt-4 text-blue-600 font-medium animate-pulse loading-dots">
              {searchType === 'location' ? 'Finding nearby parking spots' : 'Searching for parking spots'}
            </p>
          </div>
        )}

        {!searching && loading && !spots.length && (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
          </div>
        )}

        {!searching && !loading && spots.length === 0 ? (
          <div className="text-center py-12 animate-fade-in">
            <div className="bg-white rounded-lg shadow-md p-8 max-w-md mx-auto">
              <div className="text-5xl mb-4">🔍</div>
              <p className="text-gray-700 text-lg font-medium mb-2">
                {searchQuery || searchType === 'location'
                  ? "No parking spots found matching your search."
                  : "No parking spots available at the moment."}
              </p>
              <p className="text-gray-500 mb-6">Try searching for a different location or check back later.</p>
              <Button 
                onClick={() => {
                  setSearchQuery('');
                  setSearchType('text');
                  fetchParkingSpots();
                }}
                className="animate-pulse-glow"
              >
                Reset Search
              </Button>
            </div>
          </div>
        ) : (
          <>
            {currentView === 'map' ? (
              <div className="mt-8">
                <MapView 
                  spots={spots}
                  userLocation={userLocation}
                  onGetDirections={(address) => {
                    const match = spots.find((s) => s.address === address);
                    handleGetDirections(match ?? ({ address } as ParkingSpot));
                  }}
                />
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
                {spots.map((spot, index) => (
                  <div 
                    key={spot.id} 
                    className={`flex flex-col transform transition-all duration-500 ${cardsVisible ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'}`}
                    style={{ transitionDelay: getAnimationDelay(index) }}
                  >
                    <BookingDialog
                      onBook={(date, time, hours, vehicleNumber, lotCount) =>
                        handleBooking(spot.id, spot.price_per_hour, date, time, hours, vehicleNumber, lotCount)
                      }
                      parkingSpotId={spot.id}
                      maxLots={spot.parking_lot_count}
                    >
                      <div className="w-full hover:shadow-lg transform transition-all duration-300 hover:-translate-y-1 cursor-pointer">
                        <ParkingSpotCard spot={spot} />
                        {searchType === 'location' && spot.distance_km && (
                          <div className="px-4 pb-2">
                            <div className="text-xs text-blue-600 font-medium bg-blue-50 px-2 py-1 rounded-full inline-block">
                              📍 {spot.distance_km} km away
                            </div>
                          </div>
                        )}
                      </div>
                    </BookingDialog>
                    <Button
                      variant="outline"
                      className="mt-2 group"
                      onClick={() => handleGetDirections(spot)}
                    >
                      <Navigation className="w-4 h-4 mr-2 group-hover:animate-pulse" />
                      Get Directions
                      <span className="ml-1 inline-block transform group-hover:translate-x-1 transition-transform duration-300">→</span>
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Search;
